import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Package, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useCurrency } from '../components/CurrencySelector';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export const PaymentSuccessPage = () => {
  const { t, language } = useLanguage();
  const { format } = useCurrency();
  const { token } = useAuth();
  const { fetchCart } = useCart();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');

  const [state, setState] = useState({
    status: 'polling', // polling | paid | expired | failed | error
    orderId: null,
    amount: null,
    currency: 'eur',
    attempt: 0,
  });

  useEffect(() => {
    if (!sessionId) {
      setState((s) => ({ ...s, status: 'error' }));
      return;
    }
    let cancelled = false;
    let attempt = 0;

    const poll = async () => {
      attempt += 1;
      try {
        const res = await axios.get(`${API}/payments/checkout/status/${sessionId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        const { payment_status: paymentStatus, status, order_id: orderId, amount_total: amount, currency } = res.data;
        if (paymentStatus === 'paid') {
          setState({ status: 'paid', orderId, amount: amount / 100, currency, attempt });
          fetchCart();
          return;
        }
        if (status === 'expired') {
          setState({ status: 'expired', orderId, amount: null, currency: 'eur', attempt });
          return;
        }
        if (attempt >= MAX_POLL_ATTEMPTS) {
          setState({ status: 'pending_long', orderId, amount: null, currency: 'eur', attempt });
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        setState((s) => ({ ...s, status: 'error' }));
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  const renderContent = () => {
    if (state.status === 'polling') {
      return (
        <>
          <div className="w-20 h-20 bg-[#0F4C5C]/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-[#0F4C5C] animate-spin" />
          </div>
          <h1 className="text-3xl font-bold mb-4">
            {language === 'fr' ? 'Vérification du paiement...' : 'Verifying payment...'}
          </h1>
          <p className="text-muted-foreground">
            {language === 'fr'
              ? 'Merci de patienter, nous confirmons votre transaction auprès de Stripe.'
              : 'Please wait while we confirm your transaction with Stripe.'}
          </p>
        </>
      );
    }
    if (state.status === 'paid') {
      return (
        <>
          <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-10 h-10 text-green-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">
            {language === 'fr' ? 'Paiement confirmé !' : 'Payment confirmed!'}
          </h1>
          {state.amount && (
            <p className="text-2xl font-bold text-[#0F4C5C] mb-2">
              {format(state.amount)}
            </p>
          )}
          {state.orderId && (
            <>
              <p className="text-muted-foreground mb-2">
                {language === 'fr' ? 'Numéro de commande :' : 'Order number:'}
              </p>
              <p className="font-mono text-lg font-semibold text-[#0F4C5C] mb-6">
                #{state.orderId.slice(0, 8).toUpperCase()}
              </p>
            </>
          )}
          <p className="text-muted-foreground mb-8">
            {language === 'fr'
              ? 'Un email de confirmation vous a été envoyé. Les vendeurs préparent votre commande.'
              : 'A confirmation email has been sent. Vendors are preparing your order.'}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={() => navigate('/orders')} className="btn-primary" data-testid="view-orders-btn">
              <Package className="w-4 h-4 mr-2" />
              {language === 'fr' ? 'Voir mes commandes' : 'View my orders'}
            </Button>
            <Button variant="outline" onClick={() => navigate('/shop')}>
              {language === 'fr' ? 'Continuer mes achats' : 'Continue shopping'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </>
      );
    }
    if (state.status === 'expired') {
      return (
        <>
          <div className="w-20 h-20 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <XCircle className="w-10 h-10 text-orange-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">
            {language === 'fr' ? 'Session expirée' : 'Session expired'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {language === 'fr'
              ? 'Votre session de paiement a expiré. Veuillez recommencer.'
              : 'Your payment session has expired. Please try again.'}
          </p>
          <Button onClick={() => navigate('/cart')} className="btn-primary">
            {language === 'fr' ? 'Retour au panier' : 'Back to cart'}
          </Button>
        </>
      );
    }
    if (state.status === 'pending_long') {
      return (
        <>
          <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <Loader2 className="w-10 h-10 text-yellow-600" />
          </div>
          <h1 className="text-3xl font-bold mb-4">
            {language === 'fr' ? 'Paiement en cours de traitement' : 'Payment is being processed'}
          </h1>
          <p className="text-muted-foreground mb-8">
            {language === 'fr'
              ? 'Cela peut prendre quelques minutes. Vous recevrez un email dès que le paiement sera confirmé.'
              : 'This may take a few minutes. You will receive an email as soon as the payment is confirmed.'}
          </p>
          <Button onClick={() => navigate('/orders')} className="btn-primary">
            {language === 'fr' ? 'Voir mes commandes' : 'View my orders'}
          </Button>
        </>
      );
    }
    return (
      <>
        <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <XCircle className="w-10 h-10 text-red-600" />
        </div>
        <h1 className="text-3xl font-bold mb-4">
          {language === 'fr' ? 'Une erreur est survenue' : 'An error occurred'}
        </h1>
        <p className="text-muted-foreground mb-8">
          {language === 'fr'
            ? 'Nous n\'avons pas pu vérifier votre paiement. Contactez le support si vous avez été débité.'
            : 'We could not verify your payment. Contact support if you have been charged.'}
        </p>
        <Button onClick={() => navigate('/')} variant="outline">
          {language === 'fr' ? 'Retour à l\'accueil' : 'Back to home'}
        </Button>
      </>
    );
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center py-12" data-testid="payment-success-page">
      <div className="text-center max-w-md mx-auto px-4">{renderContent()}</div>
    </div>
  );
};
